import { useState, useCallback, useEffect } from 'react';

interface UsePiPOptions {
  onClose?: () => void;
}

export const usePiP = (options: UsePiPOptions = {}) => {
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported('documentPictureInPicture' in window);
  }, []);

  const closePiP = useCallback(() => {
    if (pipWindow) {
      pipWindow.close();
      setPipWindow(null);
      options.onClose?.();
    }
  }, [pipWindow, options]);

  const requestPiP = useCallback(async (width = 400, height = 300) => {
    if (!('documentPictureInPicture' in window)) {
      console.warn('Document Picture-in-Picture API is not supported.');
      return;
    }

    try {
      // Close existing if any
      if (pipWindow) {
        pipWindow.close();
      }

      const win = await window.documentPictureInPicture.requestWindow({
        width,
        height,
      });

      // Copy styles
      [...document.styleSheets].forEach((styleSheet) => {
        try {
          const cssRules = [...styleSheet.cssRules]
            .map((rule) => rule.cssText)
            .join('');
          const style = document.createElement('style');
          style.textContent = cssRules;
          win.document.head.appendChild(style);
        } catch (e) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.type = styleSheet.type;
          link.media = styleSheet.media.toString();
          link.href = styleSheet.href || '';
          win.document.head.appendChild(link);
        }
      });

      // Handle closing
      win.addEventListener('pagehide', () => {
        setPipWindow(null);
        options.onClose?.();
      });

      setPipWindow(win);
    } catch (error) {
      console.error('Failed to open PiP window:', error);
      throw error;
    }
  }, [pipWindow, options]);

  const checkAutoPiPPermission = useCallback(async () => {
    if (!('permissions' in navigator)) return 'unsupported';
    try {
      // @ts-ignore
      const status = await navigator.permissions.query({ name: 'automatic-picture-in-picture' });
      return status.state;
    } catch (error) {
      return 'unsupported';
    }
  }, []);

  return {
    pipWindow,
    requestPiP,
    closePiP,
    isSupported,
    checkAutoPiPPermission,
  };
};
